import React, { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  Chip,
  CircularProgress,
  Divider,
  FormControlLabel,
  Radio,
  RadioGroup,
  Stack,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import { Psychology, Science } from '@mui/icons-material';

import { IKiProvider } from '../../Interfaces/IKi';
import {
  useCompareKi,
  useGetKiConfig,
  useSaveKiConfig,
} from '../../hooks/reactQuerys/KiQueries';
import { useGetUsers } from '../../hooks/reactQuerys/UserQueries';
import { useAlert } from '../../context/AlertContext';

/**
 * KI-Anbindung einrichten (#1336) — unter /admin/dev/ki.
 *
 * Je Anbieter Modell und Zugangsdaten, dazu der Hauptschalter, der **alle**
 * KI-Funktionen stilllegt. Künftig übernimmt das eine Erweiterung; bis dahin
 * steht es hier, weil es sonst nirgends erreichbar wäre.
 *
 * Der API-Schlüssel wird nie geladen — der Server liefert nur, ob einer
 * hinterlegt ist. Ein leeres Feld heißt deshalb „unverändert lassen" und nicht
 * „löschen"; zum Löschen gibt es den eigenen Knopf.
 */
export const KiSettingsOverview: React.FC = () => {
  const { showAlert } = useAlert();
  const { kiConfig, isLoadingKiConfig } = useGetKiConfig();
  const { saveKiConfig, isPendingSaveKiConfig } = useSaveKiConfig();
  const { compareKi, isPendingCompareKi } = useCompareKi();
  const { users, isLoadingUsers } = useGetUsers();

  const [enabled, setEnabled] = useState(false);
  const [enabledUserArray, setEnabledUserArray] = useState<number[]>([]);
  const [userSearch, setUserSearch] = useState('');
  const [activeProvider, setActiveProvider] = useState('');
  const [draft, setDraft] = useState<Record<string, Partial<IKiProvider>>>({});
  /** Neu eingegebene Schlüssel, getrennt vom übrigen Entwurf. */
  const [keyDraft, setKeyDraft] = useState<Record<string, string>>({});
  const [compareText, setCompareText] = useState('');
  const [compareOutput, setCompareOutput] = useState<
    { Provider: string; Label: string; Text: string; Info: string }[]
  >([]);

  useEffect(() => {
    if (!kiConfig) {
      return;
    }

    setEnabled(kiConfig.Enabled);
    setEnabledUserArray(kiConfig.EnabledUserArray ?? []);
    setActiveProvider(
      kiConfig.ProviderArray.find(provider => provider.isActive)?.Provider ?? ''
    );
    setDraft({});
    setKeyDraft({});
  }, [kiConfig]);

  const providerArray = kiConfig?.ProviderArray ?? [];

  const searchTerm = userSearch.trim().toLocaleLowerCase();
  const visibleUsers = (users ?? []).filter(
    user =>
      searchTerm === '' ||
      `${user.LastName} ${user.FirstName} ${user.Email}`
        .toLocaleLowerCase()
        .includes(searchTerm)
  );

  // Die Suche blendet Zeilen aus, hebt aber keine Freischaltung auf: der
  // Haken bleibt an der id, nicht an der gerade sichtbaren Liste.
  const toggleUser = (idUser: number) =>
    setEnabledUserArray(previous =>
      previous.includes(idUser)
        ? previous.filter(entry => entry !== idUser)
        : [...previous, idUser]
    );

  const valueOf = (
    provider: IKiProvider,
    field:
      | 'Model'
      | 'BaseUrl'
      | 'ProjectId'
      | 'ProjectIdPosteingang'
      | 'ProjectIdWebsuche'
      | 'WebsucheBezeichnung'
  ) =>
    (draft[provider.Provider]?.[field] as string | undefined) ??
    provider[field] ??
    '';

  const setValue = (
    provider: string,
    field:
      | 'Model'
      | 'BaseUrl'
      | 'ProjectId'
      | 'ProjectIdPosteingang'
      | 'ProjectIdWebsuche'
      | 'WebsucheBezeichnung',
    value: string
  ) =>
    setDraft(previous => ({
      ...previous,
      [provider]: { ...previous[provider], [field]: value },
    }));

  async function handleSave(clearKeyFor?: string) {
    const uploadArray = providerArray.map(provider => {
      const entry: Record<string, unknown> = { Provider: provider.Provider };
      const changed = draft[provider.Provider] ?? {};

      (
        [
          'Model',
          'BaseUrl',
          'ProjectId',
          'ProjectIdPosteingang',
          'ProjectIdWebsuche',
          'WebsucheBezeichnung',
        ] as const
      ).forEach(field => {
        if (changed[field] !== undefined) {
          entry[field] = changed[field];
        }
      });

      // Schlüssel nur mitschicken, wenn etwas eingegeben wurde — oder
      // ausdrücklich `null`, um ihn zu entfernen.
      if (clearKeyFor === provider.Provider) {
        entry.ApiKey = null;
      } else if ((keyDraft[provider.Provider] ?? '').trim() !== '') {
        entry.ApiKey = keyDraft[provider.Provider].trim();
      }

      return entry;
    });

    try {
      await saveKiConfig({
        Enabled: enabled,
        EnabledUserArray: enabledUserArray,
        ActiveProvider: activeProvider,
        ProviderArray: uploadArray,
      });
      showAlert({
        text: 'Die KI-Einstellungen wurden gespeichert.',
        severity: 'success',
      });
    } catch {
      showAlert({
        text: 'Die KI-Einstellungen konnten nicht gespeichert werden.',
        severity: 'error',
      });
    }
  }

  async function handleCompare() {
    if (compareText.trim() === '') {
      return;
    }

    setCompareOutput([]);

    try {
      const result = await compareKi({
        Message: compareText.trim(),
        ProviderArray: providerArray.map(provider => provider.Provider),
      });

      setCompareOutput(
        result.ResultArray.map(entry => ({
          Provider: entry.Provider,
          Label: entry.ProviderLabel || entry.Provider,
          Text: entry.Error ? '' : entry.Text,
          Info: entry.Error
            ? entry.Error
            : `${entry.Model ?? '—'} · ${entry.DurationMs} ms · ${
                entry.Usage?.inputTokens ?? '—'
              }/${entry.Usage?.outputTokens ?? '—'} Token`,
        }))
      );
    } catch {
      showAlert({
        text: 'Der Vergleich konnte nicht ausgeführt werden.',
        severity: 'error',
      });
    }
  }

  if (isLoadingKiConfig) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 6 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 2 }}>
      <Stack direction='row' alignItems='center' spacing={1} sx={{ mb: 2 }}>
        <Psychology />
        <Typography variant='h5'>KI-Anbindung</Typography>
      </Stack>

      <Alert severity='info' sx={{ mb: 3 }}>
        Die Inhalte gehen <b>unverändert</b> an den gewählten Anbieter — es
        findet keine Anonymisierung statt. Wählen Sie den Anbieter deshalb
        bewusst, und schalten Sie die KI ab, solange kein Auftrag zur
        Verarbeitung vorliegt.
      </Alert>

      <Card variant='outlined' sx={{ mb: 3 }}>
        <CardContent>
          <FormControlLabel
            control={
              <Switch
                checked={enabled}
                onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                  setEnabled(event.target.checked)
                }
              />
            }
            label='KI-Funktionen aktiv'
          />
          <Typography variant='body2' color='text.secondary'>
            Ausgeschaltet schweigt jede KI-Funktion — Chat und künftige Agenten.
          </Typography>

          <Divider sx={{ my: 2 }} />

          <Typography variant='subtitle2'>
            Freigeschaltete Benutzer{' '}
            <Chip size='small' color='warning' label='nur fürs Testing' />
          </Typography>
          <Typography variant='body2' color='text.secondary' sx={{ mb: 2 }}>
            Die KI-Funktionen laufen ausschließlich für die hier angehakten
            Personen — alle anderen bemerken sie nicht, auch das Chat-Fenster
            nicht. Gedacht ist das für das Testing im Produktivsystem. Später
            übernimmt das eine Erweiterung, und dann gilt die Freischaltung für
            alle oder für niemanden.
          </Typography>

          {isLoadingUsers ? (
            <CircularProgress size={24} />
          ) : (
            <>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 2,
                  mb: 1,
                  flexWrap: 'wrap',
                }}
              >
                <TextField
                  size='small'
                  label='Benutzer suchen'
                  value={userSearch}
                  onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                    setUserSearch(event.target.value)
                  }
                  sx={{ minWidth: 260 }}
                />
                <Typography variant='body2' color='text.secondary'>
                  {enabledUserArray.length} von {(users ?? []).length}{' '}
                  freigeschaltet
                </Typography>
              </Box>

              <Box
                sx={{
                  maxHeight: 280,
                  overflowY: 'auto',
                  border: '1px solid',
                  borderColor: 'divider',
                  borderRadius: 1,
                  px: 1,
                  py: 0.5,
                }}
              >
                {visibleUsers.length === 0 ? (
                  <Typography
                    variant='body2'
                    color='text.secondary'
                    sx={{ p: 1 }}
                  >
                    Kein Benutzer gefunden.
                  </Typography>
                ) : (
                  visibleUsers.map(user => (
                    <FormControlLabel
                      key={user.idUser}
                      sx={{ display: 'flex', mr: 0 }}
                      control={
                        <Checkbox
                          size='small'
                          checked={enabledUserArray.includes(user.idUser)}
                          onChange={() => toggleUser(user.idUser)}
                        />
                      }
                      label={
                        <Box
                          sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
                        >
                          <Typography variant='body2'>
                            {user.LastName}, {user.FirstName}
                          </Typography>
                          {Boolean(user.isNotary) && (
                            <Chip size='small' label='Notar' />
                          )}
                          <Typography variant='caption' color='text.secondary'>
                            {user.Email}
                          </Typography>
                        </Box>
                      }
                    />
                  ))
                )}
              </Box>
            </>
          )}

          {enabled && enabledUserArray.length === 0 && (
            <Alert severity='warning' sx={{ mt: 2 }}>
              Es ist niemand freigeschaltet — die KI-Funktionen sind damit für
              alle Benutzer unsichtbar, obwohl der Hauptschalter an ist.
            </Alert>
          )}
        </CardContent>
      </Card>

      {providerArray.some(
        provider => provider.isActive && provider.canLookUp === false
      ) && (
        // `canLookUp` und nicht `supportsTools`: Letzteres heißt „DiNo kann
        // dem Anbieter Werkzeuge mitgeben" und ist bei watsonx dauerhaft
        // falsch — dessen Agent bringt eigene mit, die zurückrufen. Der Satz
        // stimmte also technisch und täuschte trotzdem. Belegt wird es durch
        // einen tatsächlichen Aufruf, nicht durch einen Haken.
        <Alert severity='warning' sx={{ mb: 3 }}>
          Dieser Anbieter hat <b>noch nie in DiNo nachgesehen</b>. Fragen wie
          „welche Maßnahmen habe ich heute?" beantwortet er deshalb nicht aus
          Ihren Daten, sondern fragt zurück. Allgemeine Fragen und die Zuordnung
          im Posteingang funktionieren, weil dort die nötigen Angaben schon in
          der Anfrage stehen.
          <br />
          Bringt der Agent eigene DiNo-Werkzeuge mit, verschwindet dieser
          Hinweis, sobald er das erste Mal etwas abgerufen hat.
        </Alert>
      )}

      <Typography variant='subtitle1' sx={{ mb: 1 }}>
        Anbieter
      </Typography>
      <Typography variant='body2' color='text.secondary' sx={{ mb: 2 }}>
        Der ausgewählte Anbieter beantwortet die Anfragen. Die übrigen bleiben
        eingerichtet und lassen sich für den Vergleich weiter benutzen.
      </Typography>

      <RadioGroup
        value={activeProvider}
        onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
          setActiveProvider(event.target.value)
        }
      >
        <Stack spacing={2}>
          {providerArray.map(provider => (
            <Card key={provider.Provider} variant='outlined'>
              <CardContent>
                <Stack
                  direction='row'
                  alignItems='center'
                  spacing={1}
                  sx={{ mb: 1, flexWrap: 'wrap', rowGap: 1 }}
                >
                  <FormControlLabel
                    value={provider.Provider}
                    control={<Radio disabled={!provider.isAvailable} />}
                    label={provider.Label}
                  />
                  {!provider.isAvailable && (
                    <Chip
                      size='small'
                      color='warning'
                      variant='outlined'
                      label='noch nicht angebunden'
                    />
                  )}
                  {provider.hasApiKey ? (
                    <Chip
                      size='small'
                      color='success'
                      variant='outlined'
                      label='Schlüssel hinterlegt'
                    />
                  ) : (
                    <Chip
                      size='small'
                      variant='outlined'
                      label='kein Schlüssel'
                    />
                  )}
                </Stack>

                <Stack spacing={2} sx={{ pl: { sm: 4 } }}>
                  <TextField
                    label='Modell'
                    value={valueOf(provider, 'Model')}
                    onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                      setValue(provider.Provider, 'Model', event.target.value)
                    }
                    size='small'
                    fullWidth
                    // Bei Orchestrate wählt nicht DiNo das Modell, sondern der
                    // Agent bringt seines mit. Ein Eingabefeld hier würde
                    // vortäuschen, dass sich damit etwas ändern ließe.
                    disabled={provider.Provider === 'watsonx'}
                    helperText={
                      provider.Provider === 'watsonx'
                        ? 'Bestimmt der Agent in watsonx Orchestrate. Hier steht, womit zuletzt geantwortet wurde.'
                        : provider.Hint
                    }
                  />

                  <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
                    <TextField
                      label='API-Schlüssel'
                      value={keyDraft[provider.Provider] ?? ''}
                      onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                        setKeyDraft(previous => ({
                          ...previous,
                          [provider.Provider]: event.target.value,
                        }))
                      }
                      size='small'
                      fullWidth
                      type='password'
                      autoComplete='off'
                      placeholder={
                        provider.hasApiKey
                          ? 'hinterlegt — leer lassen, um ihn zu behalten'
                          : ''
                      }
                      helperText='Wird verschlüsselt gespeichert und nie wieder ausgeliefert.'
                    />
                    {provider.hasApiKey && (
                      <Button
                        size='small'
                        color='error'
                        variant='outlined'
                        sx={{ alignSelf: 'flex-start', mt: 0.5 }}
                        disabled={isPendingSaveKiConfig}
                        onClick={() => void handleSave(provider.Provider)}
                      >
                        Schlüssel entfernen
                      </Button>
                    )}
                  </Stack>

                  {provider.Provider === 'watsonx' && (
                    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                      <TextField
                        label='Agenten-ID'
                        value={valueOf(provider, 'ProjectId')}
                        onChange={(
                          event: React.ChangeEvent<HTMLInputElement>
                        ) =>
                          setValue(
                            provider.Provider,
                            'ProjectId',
                            event.target.value
                          )
                        }
                        size='small'
                        fullWidth
                        helperText='Der Agent in watsonx Orchestrate, der die Anfragen beantwortet.'
                      />
                      <TextField
                        label='Instanz-Adresse'
                        value={valueOf(provider, 'BaseUrl')}
                        onChange={(
                          event: React.ChangeEvent<HTMLInputElement>
                        ) =>
                          setValue(
                            provider.Provider,
                            'BaseUrl',
                            event.target.value
                          )
                        }
                        size='small'
                        fullWidth
                        helperText='z. B. https://api.eu-de.watson-orchestrate.cloud.ibm.com/instances/…'
                      />
                    </Stack>
                  )}

                  {provider.Provider === 'watsonx' && (
                    <TextField
                      label='Agenten-ID für den Posteingang'
                      value={valueOf(provider, 'ProjectIdPosteingang')}
                      onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                        setValue(
                          provider.Provider,
                          'ProjectIdPosteingang',
                          event.target.value
                        )
                      }
                      size='small'
                      fullWidth
                      helperText='Leer lassen, um denselben Agenten wie für den Chat zu nehmen. Ein eigener Agent ohne eigene Anweisungen trifft die Maßnahme zuverlässiger — gemessen.'
                    />
                  )}

                  {provider.Provider === 'watsonx' && (
                    <TextField
                      label='Agenten-ID für die Websuche'
                      value={valueOf(provider, 'ProjectIdWebsuche')}
                      onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                        setValue(
                          provider.Provider,
                          'ProjectIdWebsuche',
                          event.target.value
                        )
                      }
                      size='small'
                      fullWidth
                      helperText='Der Agent, über den watsonx im Internet sucht. Leer: keine Internetsuche mit watsonx. Der Chat-Agent wird dafür nie benutzt — DiNo prüft jede Suche vorher auf Mandantendaten.'
                    />
                  )}

                  {provider.Provider === 'watsonx' && (
                    <TextField
                      label='Bezeichnung der Websuche'
                      value={valueOf(provider, 'WebsucheBezeichnung')}
                      onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                        setValue(
                          provider.Provider,
                          'WebsucheBezeichnung',
                          event.target.value
                        )
                      }
                      size='small'
                      fullWidth
                      helperText='So steht es im Chat an jeder Suche, z. B. „Google Gemini" → „Suche über Google Gemini".'
                    />
                  )}

                  {provider.Provider === 'openai' && (
                    <TextField
                      label='Endpunkt (optional)'
                      value={valueOf(provider, 'BaseUrl')}
                      onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                        setValue(
                          provider.Provider,
                          'BaseUrl',
                          event.target.value
                        )
                      }
                      size='small'
                      fullWidth
                      helperText='Leer lassen für OpenAI. Gesetzt für kompatible Server.'
                    />
                  )}
                </Stack>
              </CardContent>
            </Card>
          ))}
        </Stack>
      </RadioGroup>

      <Box sx={{ mt: 3 }}>
        <Button
          variant='contained'
          disabled={isPendingSaveKiConfig}
          onClick={() => void handleSave()}
        >
          Speichern
        </Button>
      </Box>

      <Divider sx={{ my: 4 }} />

      <Stack direction='row' alignItems='center' spacing={1} sx={{ mb: 1 }}>
        <Science />
        <Typography variant='subtitle1'>Modelle vergleichen</Typography>
      </Stack>
      <Typography variant='body2' color='text.secondary' sx={{ mb: 2 }}>
        Dieselbe Frage geht nacheinander an alle eingerichteten Anbieter. Wer
        scheitert, nimmt die anderen nicht mit — sein Fehler steht bei ihm.
      </Typography>

      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} sx={{ mb: 2 }}>
        <TextField
          label='Frage'
          value={compareText}
          onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
            setCompareText(event.target.value)
          }
          size='small'
          fullWidth
          multiline
          minRows={2}
        />
        <Button
          variant='outlined'
          sx={{ alignSelf: 'flex-start' }}
          disabled={isPendingCompareKi || compareText.trim() === ''}
          onClick={() => void handleCompare()}
        >
          {isPendingCompareKi ? 'Läuft …' : 'Vergleichen'}
        </Button>
      </Stack>

      <Stack spacing={2}>
        {compareOutput.map(entry => (
          <Card key={entry.Provider} variant='outlined'>
            <CardContent>
              <Typography variant='subtitle2'>{entry.Label}</Typography>
              <Typography
                variant='caption'
                color='text.secondary'
                display='block'
              >
                {entry.Info}
              </Typography>
              {entry.Text !== '' && (
                <Typography
                  variant='body2'
                  sx={{ mt: 1, whiteSpace: 'pre-wrap' }}
                >
                  {entry.Text}
                </Typography>
              )}
            </CardContent>
          </Card>
        ))}
      </Stack>
    </Box>
  );
};
